import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type { ChurnRecord, ChurnRun } from '@/features/churn/churn-types';

async function throwResponseError(
  response: Response,
  fallback: string
): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  throw new Error(
    errorData.detail?.user_error_message || errorData.detail || fallback
  );
}

function hasActiveRun(runs: ChurnRun[] | undefined): boolean {
  return (runs ?? []).some(
    (run) => run.status === 'pending' || run.status === 'running'
  );
}

export function useChurnRuns() {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['churn-runs'],
    queryFn: async (): Promise<ChurnRun[]> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(`${API_URL}/admin/churn/runs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        await throwResponseError(response, 'Failed to fetch churn runs');
      }
      return response.json();
    },
    refetchInterval: (query) => (hasActiveRun(query.state.data) ? 4000 : false),
  });
}

export function useChurnRecords(runId: string | null) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['churn-records', runId],
    enabled: runId !== null,
    queryFn: async (): Promise<ChurnRecord[]> => {
      const token = await getAccessTokenSilently();
      const url = `${API_URL}/admin/churn/records?run_id=${runId}`;
      const response = await apiFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 404) {
        return [];
      }
      if (!response.ok) {
        await throwResponseError(response, 'Failed to fetch churn records');
      }
      return response.json();
    },
    refetchInterval: () => {
      const runs = queryClient.getQueryData<ChurnRun[]>(['churn-runs']);
      return hasActiveRun(runs) ? 4000 : false;
    },
  });
}

export interface CreateChurnRunInput {
  months_back: number;
  inactive_days: number;
}

export function useCreateChurnRun() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateChurnRunInput): Promise<ChurnRun> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(`${API_URL}/admin/churn/runs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        await throwResponseError(response, 'Failed to start churn analysis');
      }
      return response.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['churn-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['churn-records'] });
    },
  });
}
